// lib
import { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

// styles
import style from '@aytm/app/stats/page/sidebar/components/styles/SidebarPopupLabel.module.scss'

// What the popup draws around its text -- the padding and border of its body and the gap to the label --
// until the first popup drawn has been measured.
const POPUP_CHROME = 36

// The popup opens above the label and grows upward, so a long text near the top of the screen loses its
// beginning. It is held to the lines that fit above the label, cut at the end, and never less than one.
export const calcLabelLines = (labelRect, lineHeight, chrome = POPUP_CHROME) => {
  if (!labelRect || !(0 < lineHeight)) return null

  return Math.max(1, Math.floor((labelRect.top - chrome) / lineHeight))
}

// Measured once from the first popup drawn and shared, since every label popup is drawn the same way.
const measured = { lineHeight: null, chrome: null }

const SidebarPopupLabel = ({ root, text, label }) => {
  const [show, setShow] = useState(false)
  const [lines, setLines] = useState(null)

  const labelRef = useRef(null)

  const scrollCallback = useCallback(() => setShow(false), [setShow])

  const detachScroll = useCallback(() => {
    window.removeEventListener('scroll', scrollCallback)
    window.removeEventListener('resize', scrollCallback)
    root && root.removeEventListener('scroll', scrollCallback)
  }, [root, scrollCallback])

  // Unmounting while the note is up never reaches onHide, so the listeners are let go here too;
  // otherwise the next scroll hides a label that is gone.
  useEffect(() => detachScroll, [detachScroll])

  const place = useCallback(
    () =>
      setLines(
        calcLabelLines(labelRef.current?.getBoundingClientRect(), measured.lineHeight, measured.chrome ?? undefined)
      ),
    []
  )

  // The text's line height, and what the popup adds around the text, are read off the first popup drawn;
  // the lines are counted again with them. The popup draws its text into a container it attaches and places
  // a moment later, so the reading waits a few frames for it.
  const measureText = useCallback(
    node => {
      if (!node || null != measured.chrome) return

      let frames = 10
      const measure = () => {
        const computed = getComputedStyle(node)
        const lineHeight = parseFloat(computed.lineHeight) || 1.2 * parseFloat(computed.fontSize)
        const body = node.closest('.rfd-ui-popup')
        const placed = body && !body.classList.contains('rfd-ui-popup-hidden')

        if (0 < lineHeight && placed && labelRef.current) {
          measured.lineHeight = lineHeight
          const chrome =
            labelRef.current.getBoundingClientRect().top -
            body.getBoundingClientRect().top -
            node.getBoundingClientRect().height
          if (0 < chrome) measured.chrome = chrome
          place()
        } else if (0 < frames--) requestAnimationFrame(measure)
      }
      measure()
    },
    [place]
  )

  const hasText = Array.isArray(text) || 0 < text?.length

  return (
    <>
      {hasText ? (
        <Popup
          className={style.popup}
          targetClassName={style.target}
          width="250px"
          trigger="hover"
          direction="topleft"
          // the popup redraws its content only on a change it is told about, and the line count is one
          renderRef={lines}
          portal
          show={show}
          content={() => (
            <span
              ref={measureText}
              className={style.text}
              style={lines ? { WebkitLineClamp: lines } : undefined}
              dangerouslySetInnerHTML={{ __html: Array.isArray(text) ? text.join(' / ') : text }}
            />
          )}
          onShow={() => {
            window.addEventListener('scroll', scrollCallback)
            window.addEventListener('resize', scrollCallback)
            root && root.addEventListener('scroll', scrollCallback)
          }}
          onHide={detachScroll}
        >
          <div className={style.anchor}></div>
        </Popup>
      ) : null}
      <span
        ref={labelRef}
        dangerouslySetInnerHTML={{ __html: label }}
        onMouseOver={
          hasText
            ? () => {
                place()
                setShow(true)
              }
            : null
        }
        onMouseOut={hasText ? () => setShow(false) : null}
      />
    </>
  )
}

SidebarPopupLabel.propTypes = {
  root: PropTypes.any,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  label: PropTypes.string
}

SidebarPopupLabel.displayName = 'SidebarPopupLabel'

export default SidebarPopupLabel
